import { useCallback, useEffect, useState } from 'react';
import { ApiError, type Api, type ScoreRun, type Stat } from '../api';
import type { T } from '../i18n';
import { PERM, useCan } from '../access';
import { ReadOnlyNote } from '../ui';

/** Metric, how to format, and whether the gate treats it as a max (lower is better). */
const METRICS: Array<{ key: string; pct?: boolean; lowerBetter?: boolean; scale?: number }> = [
  { key: 'naturalness', scale: 5 }, { key: 'intent', scale: 5 }, { key: 'respect', scale: 5 }, { key: 'persuasion', scale: 5 },
  { key: 'questionRate', pct: true }, { key: 'factCoverage', pct: true }, { key: 'nextStepRate', pct: true },
  { key: 'repeats', lowerBetter: true }, { key: 'silentTurns', lowerBetter: true }, { key: 'identityRepeats', lowerBetter: true },
];

const value = (run: ScoreRun, key: string): number => {
  const v = run.summary[key];
  return typeof v === 'number' ? v : (v as Stat | undefined)?.[key.endsWith('Turns') || key.endsWith('epeats') ? 'max' : 'mean'] ?? 0;
};

export const ScorecardPanel = ({ api, t, agentId }: { api: Api; t: T; agentId: string }) => {
  // ADR-071: running the tests (AI simulations) needs Agents; the results stay visible to everyone who can view.
  const can = useCan();
  const [runs, setRuns] = useState<ScoreRun[]>();
  const [running, setRunning] = useState(false);
  const [error, setError] = useState<string>();
  const load = useCallback(async () => setRuns((await api.scorecards(agentId)).items), [api, agentId]);
  useEffect(() => void load().catch(() => setError('generic')), [load]);

  const run = async () => {
    setRunning(true);
    setError(undefined);
    try {
      const { jobId } = await api.runScorecard(agentId, 1);
      for (;;) {
        await new Promise((r) => setTimeout(r, 5000));
        const j = await api.job(jobId);
        if (j.status === 'done') break;
        if (j.status === 'failed') throw new ApiError(j.error?.code ?? 'generic', 500);
      }
      await load();
    } catch (e) {
      setError(e instanceof ApiError ? e.code : 'generic');
    } finally {
      setRunning(false);
    }
  };

  const fmt = (m: (typeof METRICS)[number], v: number) => (m.pct ? `${Math.round(v * 100)}%` : m.scale ? `${v.toFixed(2)} / ${m.scale}` : v.toFixed(2));

  return (
    <div>
      <ReadOnlyNote t={t} need={PERM.agents} />
      <div className="row-between">
        <p className="muted" style={{ maxWidth: 760 }}>{t('score.hint')}</p>
        {can(PERM.agents) && <button type="button" className="primary" disabled={running} onClick={() => void run()}>{running ? t('score.running') : t('score.run')}</button>}
      </div>
      {error && <div className="banner error">{t(`error.${error}`) === `error.${error}` ? t('error.generic') : t(`error.${error}`)}</div>}
      {runs && runs.length === 0 && <div className="empty"><p>{t('score.none')}</p></div>}
      {runs && runs.length > 0 && (
        <table className="table score">
          <thead>
            <tr>
              <th />
              {runs.slice(0, 5).map((r) => (
                <th key={r.id}>
                  <div>{new Date(r.createdAt).toLocaleString()}</div>
                  <span className={`pill ${r.gate.pass ? 'published' : 'fail'}`}>{r.gate.pass ? t('score.pass') : t('score.fail')}</span>
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {METRICS.map((m) => (
              <tr key={m.key}>
                <td><strong>{t(`metric.${m.key}`)}</strong></td>
                {runs.slice(0, 5).map((r) => {
                  const failed = r.gate.failures.some((f) => f.startsWith(m.key));
                  return <td key={r.id} className={failed ? 'bad' : 'good'}>{fmt(m, value(r, m.key))}</td>;
                })}
              </tr>
            ))}
          </tbody>
        </table>
      )}
    </div>
  );
};
